import { useCallback, useEffect, useState } from 'react';
import { listConnectionReadAccess, type ConnectionReadItem } from './api';

/**
 * The company's databases (managed and native PostgreSQL), with their own access. The Connections
 * page shows them as rows of Your connections; their time limit and setup live in the panel.
 * Connected services and MCP servers share the same read grants, but they are listed by their own
 * sources, so only databases are kept here.
 */
export function useDatabases(resolved: boolean) {
  const [items, setItems] = useState<ConnectionReadItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    try {
      const result = await listConnectionReadAccess();
      setItems(result.items.filter((item) => item.provider === 'postgres'));
      setFailed(false);
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  }, []);
  // Committed account and company: a read sent before them (a deep link mounts this first) has no identity pair.
  useEffect(() => {
    if (resolved) void load();
  }, [load, resolved]);
  useEffect(() => {
    const refresh = () => void load();
    window.addEventListener('native-postgres-saved', refresh);
    return () => window.removeEventListener('native-postgres-saved', refresh);
  }, [load]);
  return {
    items,
    failed,
    load,
    update: (next: ConnectionReadItem) =>
      setItems(
        (current) =>
          current?.map((entry) => (entry.connectionId === next.connectionId ? next : entry)) ??
          current,
      ),
    drop: (connectionId: string) =>
      setItems((current) => current?.filter((entry) => entry.connectionId !== connectionId) ?? null),
  };
}

export type DatabasesState = ReturnType<typeof useDatabases>;

/** A database that its company admin manages here (Poneva-managed PostgreSQL, not a provider's). */
export const canManageDatabase = (item: ConnectionReadItem, companyAdmin: boolean) =>
  companyAdmin &&
  item.provider === 'postgres' &&
  item.canSet &&
  /^managed_pg_[a-f0-9]{40}$/.test(item.connectionId);
